import React, { Fragment, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import {
  ReceptorRecord,
  UpdateConfigRequest,
} from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { ReceptorPromiseClient } from '@trustero/trustero-api-web/lib/agent/receptor_grpc_web_pb'
import {
  useHideModal,
  useIsShowModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import { useAuthorizedGrpcClientWithContentUpdate } from 'src/adapter/grpcClient'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { ReceptorTemplate } from 'src/components/ModalForms/Receptor/ActivateReceptorForm/ActivateReceptorForm'
import {
  ModalForm,
  ModalFormId,
  ModalFormIdQueryParam,
} from 'src/components/ModalForms/ModalForm'
import { InputType } from 'src/components/ModalForms/Receptor/ActivateReceptorForm/ActivateReceptorForm.constants'
import { FieldDef, SectionDef } from 'src/xgenerated/receptor'
import { setGrcWizardReceptorModelId } from 'src/components/GrcImportWizard/GrcImportWizard.store'
import { Tabs } from '../../../../components/Reusable/Tabs'
import { getReceptorTemplate } from '../../../../xgenerated'
import { ReceptorStaticHelper } from '../../../../context/Content/statichelpers'
import { useScanReceptor } from '../../receptors.hooks'
import { WORKFLOW_RECEPTOR_MODEL_IDS } from '../../receptors.constants'
import { useReceptorConfigContext } from '../../context/ReceptorConfigContext'
import { ConfigureReceptorFormSection } from './ConfigureReceptorForm.components'
import { createDBControlMappings } from './ConfigureReceptorForm.helpers'

export const ActivateReceptorQueryParams = {
  MODAL_FORM_ID: ModalFormIdQueryParam,
  NEW_RECEPTOR: 'new_receptor',
  RECEPTOR_ID: 'receptor_id',
  RECEPTOR_MODEL_ID: 'receptor_model_id',
}

export const ConfigureReceptorForm = (): JSX.Element => {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()
  const scanReceptor = useScanReceptor()
  const {
    receptor,
    config,
    mappings,
    setMappings,
    initialMappings,
    validateMappings,
  } = useReceptorConfigContext()
  const params = queryString.parse(location.search)
  const show =
    (useIsShowModal(ModalFormId.CONFIGURE_RECEPTOR) ||
      params.modalFormId === ModalFormId.CONFIGURE_RECEPTOR) &&
    !!receptor

  const onHide = () => {
    setMappings(initialMappings.current)
    navigate(
      queryString.exclude(
        `${location.pathname}${location.search}`,
        [
          ActivateReceptorQueryParams.MODAL_FORM_ID,
          ActivateReceptorQueryParams.RECEPTOR_ID,
        ],

        {
          arrayFormat: 'bracket',
        },
      ),
    )
  }
  const hide = useHideModal({
    modalId: ModalFormId.CONFIGURE_RECEPTOR,
    onHide,
  })
  const receptorClient = useAuthorizedGrpcClientWithContentUpdate(
    ReceptorPromiseClient,
  )

  const setActiveModal = useSetActiveModal(ModalFormId.GRC_IMPORT_WIZARD)

  const receptorTemplate: ReceptorTemplate = useMemo(
    () =>
      receptor?.modelid
        ? getReceptorTemplate(receptor.modelid)
        : {
            ...new ReceptorRecord().toObject(),
            field_template: [],
            name: '',
            content: '',
            logo: null,
            service_ids: [],
            control_ids: [],
            category: '',
          },
    [receptor],
  )

  if (!receptor || !receptorTemplate.config_template) return <></>

  const sections = receptorTemplate.config_template

  const onSubmit =
    (section: SectionDef) => async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      // Submit the form
      try {
        const { fields } = section
        const skipMapValidation = fields.some(
          (field: FieldDef) =>
            field.input_type === InputType.LIST_CONFIG ||
            field.input_type === InputType.TEXT,
        )
        if (!skipMapValidation) {
          const isValid = validateMappings()
          if (!isValid) {
            return
          }
        }
        if (WORKFLOW_RECEPTOR_MODEL_IDS.includes(receptor.modelid)) {
          const request = new UpdateConfigRequest()
            .setReceptorId(receptor.id)
            .setDescription(receptor.description || '')
            .setName(receptor.name || '')
            .setConfig(JSON.stringify(config))
          await receptorClient.updateConfig(request)
        } else {
          const request = new UpdateConfigRequest()
            .setReceptorId(receptor.id)
            .setDescription(receptor.description || '')
            .setName(receptor.name || '')
            .setConfig(JSON.stringify(config))
            .setControlMappingsList(createDBControlMappings(sections, mappings))
          await receptorClient.updateConfig(request)
        }

        scanReceptor({
          receptorId: receptor.id,
          receptorModelId: receptor.modelid,
        })

        if (
          receptor.modelid === 'trr-archer' ||
          receptor.modelid === 'trr-anecdotes'
        ) {
          setGrcWizardReceptorModelId(receptor.modelid)
          setTimeout(() => {
            setActiveModal()
          }, 100)
        }

        hide()
      } catch (e) {
        showInfoToast('Error updating receptor configuration')
      }
    }

  return (
    <ModalForm
      show={show}
      hide={hide}
      formId={ModalFormId.CONFIGURE_RECEPTOR}
      title={
        // Pull out into component
        <Fragment>
          <span style={{ marginRight: '15px' }}>
            Configure {receptorTemplate.name}
          </span>
          {receptorTemplate.logo && (
            <receptorTemplate.logo width="24px" height="24px" />
          )}
        </Fragment>
      }
      submitText={'Set Configuration'}
      hideDeactivate={
        !(ReceptorStaticHelper.isAccredited(receptor) && receptor.isenabled)
      }
      showOverflow
    >
      <form
        id={ModalFormId.CONFIGURE_RECEPTOR}
        onSubmit={onSubmit(sections[activeSectionIndex])}
      >
        {sections.length > 1 ? (
          <Tabs
            tabs={sections.map((value, index) => ({
              title: value.title,
              body: (
                <ConfigureReceptorFormSection
                  key={sections[index].title}
                  section={sections[index]}
                  hideTitle={true}
                />
              ),
            }))}
            defaultActiveKey={0}
            onSelect={(eventKey: string | null) => {
              if (eventKey !== null) {
                setActiveSectionIndex(parseInt(eventKey, 10))
              }
            }}
          />
        ) : (
          <ConfigureReceptorFormSection
            key={sections[0].title}
            section={sections[0]}
            hideTitle={false}
          />
        )}
      </form>
    </ModalForm>
  )
}
